import { makeAutoObservable, reaction } from "mobx";
import { RootStore } from "./rootStore";

import { FrameClient, NodeInfo } from "@eluvio/elv-client-js/src/FrameClient";
import { CASABLANCA_TOKEN, DEV_MODE, TENANT } from "../consts";
import TenantManager from "../utils/tenantManager";
import AutoRequest from "../utils/autoRequest";

class ClientStore {
  rootStore: RootStore;
  tenantManager: TenantManager;

  loaded: boolean;
  tenantId?: string;
  fetchedAccessToken?: string;
  client?: FrameClient;
  nodesReq: AutoRequest<NodeInfo[]>;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.tenantManager = new TenantManager();

    if (DEV_MODE) {
      console.log(`Running with tenant ${TENANT}`);
      this.tenantId = TENANT;
    }
    this.loaded = false;

    // This request has no dependencies
    this.nodesReq = new AutoRequest(
      () => this.fetchNodes(),
      () => !!this.tenantId,
    );

    makeAutoObservable(this);

    reaction(
      () => this.loaded,
      () => {
        this.fetchTenantId();
        this.fetchAccessToken();
      },
    );

    // TODO: fetching with client.Fetch()
  }

  get tenantAsId(): string {
    if (this.tenantId === undefined) {
      return "";
    }
    return this.tenantManager.isTenantId(this.tenantId)
      ? this.tenantId
      : this.tenantManager.getTenantByTenantAdmin(this.tenantId) ?? "";
  }

  get tenantAsAdmin(): string {
    if (this.tenantId === undefined) {
      return "";
    }
    return this.tenantManager.isTenantAdmin(this.tenantId)
      ? this.tenantId
      : this.tenantManager.getTenantAdminByTenant(this.tenantId) ?? "";
  }

  get accessToken(): string {
    return this.fetchedAccessToken ?? CASABLANCA_TOKEN;
  }

  get nodeMapping(): Map<string, string> | null {
    const nodes = this.nodesReq.getData();
    if (nodes === null) {
      return null;
    }
    const m = new Map();
    for (const node of nodes) {
      // Ignore private nodes entirely for now
      if (!node.private && (node.services.fabric_api.urls?.length ?? 0 > 0)) {
        m.set(node.id, node.services.fabric_api.urls![0]);
      }
    }
    return m;
  }

  Initialize(): any {
    try {
      this.client = new FrameClient({
        target: window.parent,
        timeout: 30,
      });

      if (DEV_MODE) {
        // Make the client easily accessible from the dev console
        (window as any).client = this.client;
      }
    } catch (error) {
      console.error("Failed to initialize application.");
      console.error(error);
    } finally {
      this.loaded = true;
    }
  }

  *fetchTenantId(): any {
    try {
      const tenantId =
        (yield this.client?.userProfileClient.TenantContractId()) as string;
      console.log(`Fetched tenant id of ${tenantId}`);
      if (tenantId === undefined) {
        console.error(
          "Failed to fetch tenant id",
          DEV_MODE ? "If not run inside a core app, this is expected." : "",
        );
      } else {
        this.tenantId = tenantId;
      }
    } catch (error) {
      console.error(
        "Failed to fetch tenant id. If not run inside a core app, this is expected.",
        DEV_MODE ? error : "",
      );
    }
  }

  *fetchAccessToken(): any {
    try {
      const token = yield this.client?.CreateFabricToken() as string;
      //console.log(`Fetched token ${token}`);
      this.fetchedAccessToken = token;
    } catch (error) {
      console.error(
        "Failed to fetch access token. If not run inside a core app, this is expected.",
        DEV_MODE ? error : "",
      );
    }
  }

  *fetchNodes(): any {
    try {
      // The "o" acts as a filter to get all nodes, as the method is a bit broken
      const nodes = (yield this.client?.SpaceNodes({
        matchEndpoint: "o",
      })) as NodeInfo[];
      this.nodesReq.reportData(nodes);
    } catch (error) {
      this.nodesReq.reportError();
    }
  }
}

export default ClientStore;
